import { FC, Fragment, useEffect, useState } from 'react';
import { matchPath, useHistory } from 'react-router-dom';

import { Sidebar, SideNav } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { ApiGroupModel, ApiModel, DOCUMENTATION_API_TOKEN } from '../../api/documentation.api';
import { overviews } from '../assets/overviews';

const ApiSideNavItem: FC<{ api: ApiModel }> = ({ api }) => {
    const history = useHistory();

    const path = `/docs/apis/${api.id}`;
    const active = !!matchPath(history.location.pathname, path);
    const links = [
        overviews.has(api.id) ? { path: `${path}/overview`, text: 'Overview' } : null,
        { path: `${path}/endpoints`, text: 'API Endpoints' },
        api.hasWebhooks ? { path: `${path}/webhooks`, text: 'Webhooks' } : null,
    ]
        .filter(link => link !== null)
        .map(link => link!);

    return links.length === 1 ? (
        <SideNav.Item
            active={active}
            onClick={() => {
                history.push(links[0].path);
            }}
        >
            {api.displayName}
        </SideNav.Item>
    ) : (
        <SideNav.Item active={active} collapsible={{ expanded: active }}>
            {api.displayName}
            <SideNav>
                {links.map(link => (
                    <SideNav.Item
                        key={link.path}
                        active={!!matchPath(history.location.pathname, link.path)}
                        onClick={() => {
                            history.push(link.path);
                        }}
                    >
                        {link.text}
                    </SideNav.Item>
                ))}
            </SideNav>
        </SideNav.Item>
    );
};

const ApiGroupSideNavItem: FC<{ apiGroup: ApiGroupModel }> = ({ apiGroup }) => (
    <Fragment>
        {apiGroup.apis.map(api => (
            <ApiSideNavItem key={api.id} api={api} />
        ))}
    </Fragment>
);

export const ApiListSidebar: FC = () => {
    const [documentationApi] = useDependencies(DOCUMENTATION_API_TOKEN);
    const [apiGroups, setApiGroups] = useState<ApiGroupModel[]>([]);

    useEffect(() => {
        documentationApi.getApis().then(setApiGroups);
    }, [documentationApi]);

    return (
        <Sidebar localStorageKey="sidebar:docs">
            <SideNav>
                {apiGroups.length === 1 ? (
                    <ApiGroupSideNavItem apiGroup={apiGroups[0]} />
                ) : (
                    apiGroups.map(apiGroup => (
                        <SideNav.Group key={apiGroup.displayName} title={apiGroup.displayName}>
                            <ApiGroupSideNavItem apiGroup={apiGroup} />
                        </SideNav.Group>
                    ))
                )}
            </SideNav>
        </Sidebar>
    );
};
ApiListSidebar.displayName = 'ApiListSidebar';
